import { captureException } from '@sentry/react'
import { getGiftChecker_API } from 'api'
import { TAppDispatch, TGetState } from 'store/types'
import { ApiError, ReduxThunkError } from 'utils/errors'
import slice from 'store/features/aiAvatar/slice'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import { userSelectors } from 'store/features/user/selectors'

const { actions } = slice

// Checks the free-trial "gift" eligibility for the current user and stores it in
// redux. Runs only for free users (the gift concept doesn't apply to paid ones)
// and de-dupes while a request is in flight. Intentionally NOT data-cached: it
// re-checks on every AI Avatar tab open and after each successful generation,
// since using the trial changes eligibility. Failures are silent to the user
// (captured to Sentry, no toast) — this is a background check.
const checkGiftAccess =
  () => async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    if (!userSelectors.isFreeUser(state)) return
    if (aiAvatarSelectors.isGiftAccessLoading(state)) return

    dispatch(actions.setGiftAccessLoading(true))
    try {
      const response = await getGiftChecker_API()
      dispatch(actions.setGiftAccess(response.data))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      // A failed check is a *definitive (if unknown) result*, not "still
      // pending". Fall back to the neutral free-trial state so the generate
      // footer doesn't stay permanently disabled — it gates on
      // `giftAccess === null` to avoid a flash while the check is in flight, and
      // a null left by a failure would never clear (see `useAvatarGenerate`).
      // `isAccessible: true` keeps the free-trial CTA; the server stays the
      // source of truth on the actual generate click.
      dispatch(
        actions.setGiftAccess({ isAccessible: true, accessibleDuration: 0 })
      )
    } finally {
      dispatch(actions.setGiftAccessLoading(false))
    }
  }

export const giftAccessThunks = {
  checkGiftAccess
}
